/**
 * GlassConfirmPrompt — Liquid glass confirmation dialog for page actions.
 *
 * "Allow the agent to scroll to the pricing section?"
 * [Yes] [No]
 *
 * Auto-declines after 10 seconds. Spring-animated.
 */

import { createGlassPanel, createGlassButton, glassText } from '../glass'
import { springEnter, springExit, type SpringAnimation } from '../spring'
import { injectTokens, tokens } from '../tokens'
import { autoDetectAndApply } from '../themeDetector'

const AUTO_DECLINE_MS = 10_000

export class GlassConfirmPrompt {
  private el: HTMLDivElement | null = null
  private currentAnim: SpringAnimation | null = null
  private autoTimer: ReturnType<typeof setTimeout> | null = null
  private resolver: ((result: boolean) => void) | null = null

  /** Show a confirmation prompt. Returns a promise that resolves true/false. */
  ask(description: string): Promise<boolean> {
    injectTokens()
    this.dismiss()

    return new Promise<boolean>((resolve) => {
      this.resolver = resolve

      this.el = createGlassPanel({
        radius: 'lg',
        elevation: 'elevated',
        role: 'alertdialog',
        className: 'hs-confirm-prompt',
      }) as HTMLDivElement

      Object.assign(this.el.style, {
        position: 'fixed',
        bottom: '320px',
        left: '50%',
        transform: 'translateX(-50%)',
        padding: 'var(--hs-space-5)',
        maxWidth: '340px',
        minWidth: '240px',
        zIndex: tokens.zTop,
        opacity: '0',
        textAlign: 'center',
      })

      this.el.setAttribute('aria-label', 'Action confirmation')
      autoDetectAndApply(this.el)

      // Description
      const desc = document.createElement('p')
      glassText(desc, 'primary', 'base', 'normal')
      desc.textContent = description
      desc.style.marginBottom = 'var(--hs-space-4)'
      this.el.appendChild(desc)

      // Buttons
      const actions = document.createElement('div')
      Object.assign(actions.style, {
        display: 'flex',
        gap: 'var(--hs-space-3)',
        justifyContent: 'center',
      })

      actions.appendChild(createGlassButton({
        label: 'Yes',
        variant: 'accent',
        size: 'md',
        onClick: () => this.resolve(true),
      }))

      actions.appendChild(createGlassButton({
        label: 'No',
        variant: 'default',
        size: 'md',
        onClick: () => this.resolve(false),
      }))

      this.el.appendChild(actions)
      document.body.appendChild(this.el)

      this.currentAnim = springEnter(this.el, { preset: 'snappy' })

      // Auto-decline
      this.autoTimer = setTimeout(() => this.resolve(false), AUTO_DECLINE_MS)
    })
  }

  /** Dismiss any active prompt (resolves as declined). */
  dismiss(): void {
    if (this.resolver) {
      this.resolver(false)
      this.resolver = null
    }
    this.cleanup()
  }

  private resolve(result: boolean): void {
    this.resolver?.(result)
    this.resolver = null
    this.cleanup()
  }

  private cleanup(): void {
    if (this.autoTimer) { clearTimeout(this.autoTimer); this.autoTimer = null }
    this.currentAnim?.stop()

    if (this.el) {
      const el = this.el
      this.el = null
      this.currentAnim = springExit(el, { remove: true, preset: 'smooth' })
    }
  }
}
